// ahri_v5 — domain/ahriRecords.js

import { prettifyAttributeKey } from "../ahriSystemSelection.js";
import { parsePayloadJsonDoc } from "./solrDocs.js";
import { humanizeFieldName } from "./formatting.js";

export function formatDetailValue(value) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const flat = value
      .map((entry) => (entry === null || entry === undefined ? "" : String(entry).trim()))
      .filter(Boolean);
    return flat.join(", ");
  }
  if (value && typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  const text = value === null || value === undefined ? "" : String(value).trim();
  return text;
}

export function isTruthyLike(value) {
  const normalized = String(value === null || value === undefined ? "" : value).trim().toLowerCase();
  return ["true", "1", "yes", "y", "on"].includes(normalized);
}

export function getAhriAttributeIconType(key, value) {
  const field = String(key || "").trim().toLowerCase();
  if (!field) return "default";
  if (field.includes("energystar")) return isTruthyLike(value) ? "energy" : "energy-off";
  if (field.includes("seer")) return "seer";
  if (field.includes("hspf")) return "hspf";
  if (field.includes("eer")) return "eer";
  if (field.includes("referenceid") || field.endsWith("id")) return "id";
  if (field.includes("brand")) return "brand";
  if (field.includes("modelnumber") || field.includes("model")) return "model";
  if (field.includes("match")) return "match";
  if (field.includes("capacity") || field.includes("tonnage")) return "capacity";
  return "default";
}

// Stored as normalized keys (lowercase, alphanumeric only) so the lookup is
// resilient to underscores/spaces/camelCase variants of the same field.
const AHRI_RECORD_EXCLUDED_KEYS = new Set([
  "matchedproductattributes",
  "productmapping",
  "productmappingmeta",
  "matchedproducts",
  "productsavailablebylocation",
  "ahricompletematch",
  "ahripartialmatch",
  "ahrimatchstatus",
  "completematch",
  "partialmatch",
  "matchstatus",
  "featuredgroupnames",
  "featuredgroupkeys",
  "featuredgrouptypes",
  "featuredgrouptags",
  "featuredgroupstartat",
  "featuredgroupendat",
  "featuredgroupactive",
  "featured",
  "featuredat",
  "importjobid",
  "searchtext",
  "searchtextfraction",
  "doctypes",
  "accountid",
  "version",
  "id",
  "payloadjson",
  "matchedproductattributesjson",
  "availabilitymaxpct",
  "availabilitywarehousecount",
  // Brand/model/status fields — already shown on the product cards above.
  "indoorbrandname",
  "outdoorbrandname",
  "furnacebrandname",
  "brandname",
  "indoormodelnumber",
  "outdoormodelnumber",
  "furnacemodelnumber",
  "modelnumber",
  "modelstatus",
  "manufacturertype",
  "isenergystar",
  "isrerated",
]);
export function normalizeAhriRecordKey(key) {
  return String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** True when a payload/Solr key is mapping, stock, match, or platform metadata — not AHRI cert data. */
export function isAhriRecordExcludedKey(key) {
  const raw = String(key || "").trim();
  if (!raw || raw.startsWith("_")) return true;
  const normalized = normalizeAhriRecordKey(raw);
  if (!normalized || AHRI_RECORD_EXCLUDED_KEYS.has(normalized)) return true;
  // Match flags (ahri_complete_match, partial_match, match_status, etc.)
  if (normalized.includes("completematch") || normalized.includes("partialmatch")) return true;
  if (normalized.includes("matchstatus") || normalized.endsWith("match") || normalized.endsWith("matched")) return true;
  // Availability / inventory / buildability (available_at_014, availability_max_pct, ...)
  if (normalized.startsWith("availableat") || normalized.startsWith("buildableat")) return true;
  if (normalized.startsWith("availability") || normalized.includes("availableat") || normalized.includes("buildableat")) return true;
  if (normalized.includes("warehouse") || normalized.includes("inventory") || normalized.includes("stock")) return true;
  if (normalized.includes("available") && (normalized.includes("location") || normalized.includes("warehouse") || normalized.includes("count") || normalized.includes("pct"))) return true;
  // Product mapping / matched product attributes
  if (normalized.includes("prodmap") || normalized.includes("prodattr")) return true;
  if (normalized.includes("productmapping") || normalized.includes("matchedproduct")) return true;
  // Featured-group platform metadata
  if (normalized.includes("featuredgroup") || normalized === "featured" || normalized.startsWith("featured")) return true;
  // Brand / model / status fields — redundant with the product cards.
  if (normalized.includes("brandname") || normalized.includes("modelnumber") || normalized.includes("modelstatus")) return true;
  if (normalized.includes("manufacturertype")) return true;
  if (normalized === "isenergystar" || normalized === "isrerated") return true;
  // Per-slot product code suffixes (outdoormodelnumber_product1, etc.)
  if (/product\d+$/.test(normalized)) return true;
  return false;
}

/** Only scalar or flat string-list AHRI values belong in the cert details table. */
export function isDisplayableAhriRecordValue(value) {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) {
    if (!value.length) return false;
    if (value.some((entry) => entry && typeof entry === "object")) return false;
  } else if (typeof value === "object") {
    return false;
  }
  return String(formatDetailValue(value)).trim() !== "";
}

/** Build shopper-facing AHRI certificate rows from payload_json (preferred) or filtered Solr rows_*. */
export function buildAhriRecordDetailRows(sourceDoc) {
  if (!sourceDoc || typeof sourceDoc !== "object") return [];
  const payload = parsePayloadJsonDoc(sourceDoc);
  if (payload && typeof payload === "object") {
    return Object.entries(payload)
      .filter(([key, value]) => !isAhriRecordExcludedKey(key) && isDisplayableAhriRecordValue(value))
      .map(([key, value]) => ({
        key,
        label: prettifyAttributeKey(key) || humanizeFieldName(key),
        value: formatDetailValue(value),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  const excludedPrefixes = [
    "rows_prodmap_",
    "rows_prodattr_",
    "rows_featured_",
    "rows_inventory_",
    "rows_available_",
    "rows_available_at_",
    "rows_buildable_at_",
    "rows_availability_",
    "rows_warehouse_",
    "rows_selected_warehouse_",
    "rows_other_warehouse_",
    "rows_products_available_",
    "rows_matched_",
  ];
  const excludedContains = [
    "prodmap",
    "prodattr",
    "inventory",
    "warehouse",
    "available_at",
    "buildable_at",
    "availability_",
    "available",
    "featured",
    "matched",
    "mapping",
    "stock",
    "product_mapping",
  ];
  return Object.entries(sourceDoc)
    .filter(([key, value]) => {
      const fieldKey = String(key || "").trim();
      if (!fieldKey.startsWith("rows_")) return false;
      if (excludedPrefixes.some((prefix) => fieldKey.startsWith(prefix))) return false;
      if (excludedContains.some((token) => fieldKey.toLowerCase().includes(token))) return false;
      if (isAhriRecordExcludedKey(fieldKey.replace(/^rows_/, ""))) return false;
      return isDisplayableAhriRecordValue(value);
    })
    .map(([key, value]) => ({
      key,
      label: humanizeFieldName(key),
      value: formatDetailValue(value),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
